import { memoize, minBy } from 'lodash';
import { BI_DEPRECATED_COMPLEX_SELECTOR, createBiLogger, reportBi, getEditorTypeForBI, } from '../../bi';
export function createSuperAPIFactory(
// NOTE: hostType is ignored in that case, however, it could be used to return a different SuperAPI per host
// @ts-expect-error
hostType, 
// @ts-expect-error
hostAPI, ds) {
    return (context) => {
        const appDefinitionId = context.appDefinitionId;
        try {
            createBiLogger({
                userId: ds.generalInfo.getUserInfo().userId,
                metaSiteId: ds.generalInfo.getMetaSiteId(),
                editorType: getEditorTypeForBI(),
            });
        }
        catch (e) {
            console.error('failed to create bi logger', e);
        }
        async function waitForChangesApplied() {
            return new Promise((resolve) => ds.waitForChangesApplied(resolve));
        }
        function isRefComponent(compRef) {
            return (ds.components.getType(compRef) ===
                'wysiwyg.viewer.components.RefComponent');
        }
        function validateReferredComponent(compRef) {
            if (!ds.components.refComponents.isReferredComponent(compRef)) {
                throw new CompIsNotAReferredCompError(compRef.id);
            }
            // // TODO: try to understand whether or not we need to check the parent comp
            const hostRef = ds.components.refComponents.getRefHostCompPointer(compRef);
            if (!hostRef) {
                // should never happen in fact if component passes "isReferredComponent" check
                throw new UnableToFindAnOwnerError(compRef.id);
            }
            if (ds.components.data.get(hostRef)?.appDefinitionId !==
                context.appDefinitionId) {
                throw new NotAllowedToChangeRefComponent(compRef.id);
            }
        }
        function getGhostComponents(compRef) {
            return ds.components.refComponents.getAllGhostRefComponentsPrimaryConnection(compRef);
        }
        function getFirstRefComponent(compRef) {
            if (isRefComponent(compRef)) {
                return compRef;
            }
            const refComponent = ds.components.refComponents.getRefHostCompPointer(compRef);
            if (!refComponent) {
                throw new UnableToFindRefComponent(compRef.id);
            }
            return refComponent;
        }
        const buildComponentTree = memoize((componentRef) => {
            const connection = ds.platform.controllers.connections.getPrimaryConnection(componentRef);
            return {
                componentRef,
                role: connection?.role,
                children: ds.deprecatedOldBadPerformanceApis.components
                    .getChildren(componentRef)
                    .map(buildComponentTree),
            };
        }, (compRef) => compRef.id);
        const findComponentBySelectorInternal = (item, selectors, nestingLevel = 0) => {
            const topSelector = selectors[0];
            if (!topSelector) {
                return [];
            }
            if (topSelector === item.role) {
                selectors.shift();
            }
            if (selectors.length === 0) {
                return [{ item, nestingLevel }];
            }
            if (!item.children?.length) {
                return [];
            }
            const childResults = item.children?.map((child) => {
                return findComponentBySelectorInternal(child, selectors.slice(), nestingLevel++);
            });
            return childResults.flatMap((x) => x);
        };
        const findComponentBySelector = (rootCompRef, selector, allowComplexSelector = true) => {
            const roles = selector
                .split(' ')
                .map((x) => x.trim())
                .map((x) => x.slice(1));
            if (!allowComplexSelector && roles.length > 1) {
                reportBi(BI_DEPRECATED_COMPLEX_SELECTOR, {});
                console.warn('Complex selectors are deprecated and will be removed in the future. Please use getNested functionality instead.');
            }
            const componentTree = buildComponentTree(rootCompRef);
            const foundElements = findComponentBySelectorInternal(componentTree, roles);
            return minBy(foundElements, (x) => x.nestingLevel)?.item;
        };
        class RefComponentsSuperAPI {
            findComponentBySelector(compRef, selector) {
                const component = findComponentBySelector(compRef, selector);
                if (!component) {
                    throw new UnableToFindComponentBySelector(selector);
                }
                return component.componentRef;
            }
            async hideReferredComponent(compRef) {
                validateReferredComponent(compRef);
                ds.runInContext(appDefinitionId, () => {
                    return ds.components.remove(compRef);
                });
                await waitForChangesApplied();
            }
            async showReferredComponent(compRef) {
                validateReferredComponent(compRef);
                ds.runInContext(appDefinitionId, () => {
                    return ds.components.refComponents.unGhostifyComponent(compRef);
                });
                await waitForChangesApplied();
            }
            async hideBySelector(rootComp, selector) {
                const componentToHide = findComponentBySelector(rootComp, selector, false);
                if (!componentToHide) {
                    throw new UnableToFindComponentBySelector(selector);
                }
                await this.hideReferredComponent(componentToHide.componentRef);
            }
            async showBySelector(rootComp, selector) {
                const componentToShow = findComponentBySelector(rootComp, selector, false);
                if (!componentToShow) {
                    throw new UnableToFindComponentBySelector(selector);
                }
                await this.showReferredComponent(componentToShow.componentRef);
            }
            async getHiddenComponents(refComponentRef, options) {
                if (!isRefComponent(refComponentRef)) {
                    throw new UnableToGetHiddenNoneRefComponents(refComponentRef.id);
                }
                const refComponents = [refComponentRef];
                if (options?.includeNested) {
                    const children = ds.deprecatedOldBadPerformanceApis.components.getChildren(refComponentRef, true);
                    children.forEach((compRef) => {
                        if (isRefComponent(compRef)) {
                            refComponents.push(compRef);
                        }
                    });
                }
                return refComponents.reduce((acc, compRef) => {
                    const ghostsComponents = getGhostComponents(compRef);
                    if (ghostsComponents) {
                        Object.keys(ghostsComponents).forEach((ghostId) => {
                            acc.push({
                                componentRef: ds.components.get.byId(ghostId),
                                role: ghostsComponents[ghostId]?.role,
                            });
                        });
                    }
                    return acc;
                }, []);
            }
            async getHiddenComponentRoles(rootComp, options) {
                const refComponent = getFirstRefComponent(rootComp);
                const hiddenComponents = await this.getHiddenComponents(refComponent, options);
                return hiddenComponents.map((hiddenComponent) => hiddenComponent.role);
            }
            async findHiddenComponentByRole(rootComp, role, options) {
                const refComponent = getFirstRefComponent(rootComp);
                const hiddenComponents = await this.getHiddenComponents(refComponent, options);
                const hiddenComponent = hiddenComponents.find((component) => component.role === role);
                return hiddenComponent?.componentRef || null;
            }
            async isHiddenBySelector(rootComp, selector) {
                const bySelectorComponent = findComponentBySelector(rootComp, selector);
                if (!bySelectorComponent) {
                    return false;
                }
                const allHiddenComponents = await this.getHiddenComponentRoles(rootComp, {
                    includeNested: true,
                });
                return allHiddenComponents.some((hiddenComponent) => hiddenComponent === bySelectorComponent.role);
            }
        }
        return {
            refComponents: new RefComponentsSuperAPI(),
        };
    };
}
export class CompIsNotAReferredCompError extends Error {
    constructor(compId) {
        super(`The component with id ${compId} isn't a referred component`);
    }
}
export class UnableToFindAnOwnerError extends Error {
    constructor(compId) {
        super(`Unable to find an an owner of component with id ${compId}`);
    }
}
export class NotAllowedToChangeRefComponent extends Error {
    constructor(compId) {
        super(`The component with id '${compId}' doesn't belong to this app.`);
    }
}
export class UnableToFindComponentBySelector extends Error {
    constructor(selector) {
        super(`Unable to find component by selector: ${selector}`);
    }
}
export class UnableToGetHiddenNoneRefComponents extends Error {
    constructor(compId) {
        super(`Unable to find hidden component's for component with id ${compId}, the component is not a ref component`);
    }
}
export class UnableToFindRefComponent extends Error {
    constructor(rootCompId) {
        super(`Unable to find refComponent for ${rootCompId}`);
    }
}
//# sourceMappingURL=refComponents.super.js.map